import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

/**
 * MagneticButton — element pulls toward cursor within range.
 * Works with <a> or <button>; pass `as` prop.
 */
export default function MagneticButton({
  as: Comp = "a",
  children,
  className = "",
  strength = 0.35,
  range = 120,
  ...rest
}) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 280, damping: 20, mass: 0.6 });
  const sy = useSpring(y, { stiffness: 280, damping: 20, mass: 0.6 });

  const tx = useTransform(sx, (v) => v);
  const ty = useTransform(sy, (v) => v);

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = e.clientX - cx;
    const dy = e.clientY - cy;
    const dist = Math.hypot(dx, dy);
    if (dist < range) {
      x.set(dx * strength);
      y.set(dy * strength);
    } else {
      x.set(0);
      y.set(0);
    }
  };
  const onLeave = () => {
    x.set(0);
    y.set(0);
  };

  const MotionComp = motion(Comp);
  return (
    <MotionComp
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ x: tx, y: ty }}
      className={className}
      {...rest}
    >
      <motion.span
        style={{ x: useTransform(sx, (v) => v * 0.4), y: useTransform(sy, (v) => v * 0.4) }}
        className="inline-flex items-center gap-3"
      >
        {children}
      </motion.span>
    </MotionComp>
  );
}
